<template>
  <div>
    <!-- Stripe 支付方式标题行 -->
    <div class="pay_type">
      <div class="pay_type_left">
        <img
          src="@/assets/order/icon_payment_union.png"
          alt=""
          class="stripe_icon"
        />
        <span class="font12 font_weight400 colo0 line_height_17 stripe_font">
          {{ payTitleText }}
        </span>
      </div>
      <img :src="selectIcon" alt="" class="select_icon" />
    </div>

    <!-- Stripe Payment Element (支持自动显示 Stripe 已保存卡 + 垂直分割单选) -->
    <div class="stripe_card_wrap">
      <div v-show="loading" class="stripe_loading">
        <span class="stripe_spinner"></span>
        <span class="stripe_loading_text">正在初始化支付...</span>
      </div>
      <div v-show="errorMessage" class="stripe_error_msg">
        {{ errorMessage }}
      </div>

      <!-- Stripe Payment Element 垂直单选列表容器 -->
      <div
        id="payment-element"
        class="stripe_payment_element"
        v-show="!loading && clientSecret"
      ></div>
    </div>

    <!-- 底部悬浮的确认支付按钮 -->
    <div class="bottom_btn_wrap">
      <div
        class="save_btn font_weight500 font14 colof line_height_20"
        :class="{ save_btn_disabled: paying || loading }"
        @click="handlePay"
      >
        {{ paying ? "支付处理中..." : "确认支付" }}
      </div>
    </div>
  </div>
</template>

<script>
const wx = require("jweixin-module");

export default {
  name: "StripePay",
  props: {
    userOrder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectIcon: require("@/assets/login/select@2x.png"),
      stripe: null,
      elements: null,
      paymentElement: null,
      loading: false,
      paying: false,
      errorMessage: "",
      clientSecret: "",
      lastInitOrderSn: "",
      pollTimer: null,
      pollCount: 0,
      isPaymentCompleteHandled: false,
      toastInstance: null,
    };
  },
  computed: {
    payTitleText() {
      if (this.isMiniProgram()) {
        return "信用卡支付";
      }
      return "选择支付方式";
    },
  },
  mounted() {
    const orderSn = this.$route.query.orderSn;
    if (orderSn) {
      this.initStripePayment(orderSn);
    }
  },
  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },
  methods: {
    isAppleDevice() {
      const ua = navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod|macintosh/.test(ua);
    },

    isMiniProgram() {
      if (this.$route.query.whereFrom === "miniApp") {
        return true;
      }
      const ua = navigator.userAgent.toLowerCase();
      if (ua.indexOf("miniprogram") > -1) {
        return true;
      }
      if (window.__wxjs_environment === "miniprogram") {
        return true;
      }
      return false;
    },

    async initStripePayment(orderSn) {
      if (this.lastInitOrderSn === orderSn) {
        return;
      }
      this.lastInitOrderSn = orderSn;

      this.loading = true;
      this.errorMessage = "";

      try {
        const isFromValet =
          this.$route.query.fromValet == 1 ||
          (this.$routerStack &&
            this.$routerStack.includes("/user/order/fast/qqgpay"));

        const data = await this.$store.dispatch("order/createStripePayment", {
          params: {
            orderSn,
            fromValet: isFromValet ? 1 : undefined,
            // 小程序环境限制只支持信用卡支付，H5 环境不传以保留 Google Pay / Alipay 等
            ...(this.isMiniProgram()
              ? { extData: { paymentMethodTypes: ["card"] } }
              : {}),
          },
        });

        if (!data || !data.clientToken) {
          this.errorMessage = "获取支付信息失败，请返回重试";
          return;
        }

        this.clientSecret = data.clientToken;

        if (!window.Stripe) {
          this.errorMessage = "Stripe SDK 加载失败，请刷新页面重试";
          return;
        }

        this.stripe = window.Stripe(this.$stripePublishableKey);

        const elementsOptions = {
          clientSecret: this.clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#bf9264",
              borderRadius: "8px",
              fontFamily: '"Outfit", -apple-system, sans-serif',
            },
          },
        };

        // 核心：传入 customerSessionClientSecret 安全拉取 Stripe 已保存卡片
        if (
          !isFromValet &&
          data.customerSessionSecret &&
          data.customerSessionSecret.startsWith("cuss_")
        ) {
          elementsOptions.customerSessionClientSecret =
            data.customerSessionSecret;
        }

        this.elements = this.stripe.elements(elementsOptions);

        // 使用 accordion 布局：既能安全展示 Stripe 已保存的卡，又能垂直分割支付宝/Google Pay
        this.paymentElement = this.elements.create("payment", {
          layout: {
            type: "accordion",
            defaultCollapsed: false,
            radios: true,
          },
          /* 显式开启 Web 端 Apple Pay 与 Google Pay 的自动检测渲染 */
          wallets: {
            googlePay: "auto",
            applePay: "auto",
          },
          paymentMethodOrder: ["alipay", "apple_pay", "google_pay", "card"],
          defaultValues: {
            billingDetails: {
              address: {
                country: "AU",
              },
            },
          },
        });

        this.paymentElement.mount("#payment-element");

        this.paymentElement.on("ready", () => {
          this.loading = false;
        });

        this.paymentElement.on("change", (event) => {
          if (event.error) {
            this.errorMessage = event.error.message;
          } else {
            this.errorMessage = "";
          }
        });
      } catch (err) {
        this.lastInitOrderSn = "";
        this.errorMessage =
          (err && err.message) || "初始化支付失败，请返回重试";
      } finally {
        this.loading = false;
      }
    },

    /**
     * 用户点击"确认支付"
     * 调用 stripe.confirmPayment 完成支付（兼容已保存卡、支付宝及快捷支付）
     */
    async handlePay() {
      if (this.paying || this.loading) return;

      if (!this.stripe || !this.paymentElement) {
        this.$toast("支付尚未初始化，请稍候");
        return;
      }

      this.paying = true;
      this.errorMessage = "";

      this.toastInstance = this.$toast.loading({
        message: "支付处理中...",
        forbidClick: true,
        duration: 0,
      });

      try {
        const orderSn = this.userOrder.orderSn || this.$route.query.orderSn;

        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            /* 支付成功后重定向地址 */
            return_url: `${window.location.origin}/order/detail?orderSn=${orderSn}`,
          },
          /* 允许重显已保存卡片 */
          allow_redisplay: "always",
          /* 设为 false 则不自动重定向，可以手动处理结果 */
          redirect: "if_required",
        });

        if (error) {
          if (this.toastInstance) {
            this.toastInstance.clear();
          }
          this.errorMessage = error.message || "支付失败，请重试";
          this.$toast(this.errorMessage);
        } else if (paymentIntent) {
          if (
            paymentIntent.status === "succeeded" ||
            paymentIntent.status === "processing"
          ) {
            this.startPollingPaymentStatus(orderSn);
          } else {
            if (this.toastInstance) {
              this.toastInstance.clear();
            }
            this.errorMessage = "支付未完成，请重试";
            this.$toast(this.errorMessage);
          }
        }
      } catch (err) {
        if (this.toastInstance) {
          this.toastInstance.clear();
        }
        this.errorMessage = (err && err.message) || "支付过程出错，请重试";
        this.$toast(this.errorMessage);
      } finally {
        this.paying = false;
      }
    },

    // 开始轮询支付状态，直到后台支付状态更新或满 5 次
    startPollingPaymentStatus(orderSn) {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
      }
      this.pollCount = 0;
      this.isPaymentCompleteHandled = false;

      // 立即发起第一次检查
      this.checkPaymentStatus(orderSn);

      // 每隔 2 秒发起一次检查，最多检查 5 次
      this.pollTimer = setInterval(() => {
        if (this.pollCount < 5) {
          this.checkPaymentStatus(orderSn);
        } else {
          clearInterval(this.pollTimer);
          // 超过 5 次，保底跳转流转
          this.handlePaymentComplete(orderSn);
        }
      }, 2000);
    },
    // 检查支付状态并执行相应流转
    async checkPaymentStatus(orderSn) {
      this.pollCount++;
      try {
        const data = await this.$store.dispatch("order/getPaymentInfo", {
          params: { orderSn },
        });

        const status =
          data && data.status !== undefined ? parseInt(data.status, 10) : 0;

        // 状态 11 代表已支付，或者达到了 5 次限制，执行跳转流转
        if (status === 11 || this.pollCount >= 5) {
          this.handlePaymentComplete(orderSn);
        }
      } catch (err) {
        console.error("轮询支付状态接口报错：", err);
        if (this.pollCount >= 5) {
          this.handlePaymentComplete(orderSn);
        }
      }
    },
    // 停止轮询并执行最后的成功跳转或后退逻辑
    handlePaymentComplete(orderSn) {
      if (this.isPaymentCompleteHandled) return;
      this.isPaymentCompleteHandled = true;
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }

      // 🔑 轮询结束，出最终结果，清除 Loading 状态并弹出成功 Toast
      if (this.toastInstance) {
        this.toastInstance.clear();
      }
      this.$toast.success("支付成功");

      // 执行原来的跳转/后退页面逻辑
      if (this.isMiniProgram()) {
        const hasDetail = this.$route.query.hasDetail;
        if (hasDetail == 1 || hasDetail === "1") {
          wx.miniProgram.postMessage({
            data: {
              action: "StripePayResult",
              status: "success",
              orderSn: orderSn,
              hasDetail: 1,
            },
          });
          wx.miniProgram.navigateBack();
          return;
        }

        const successUrl = `/pages/ordercenter/orderdetail/orderdetail?orderSn=${orderSn}`;
        wx.miniProgram.redirectTo({
          url: successUrl,
        });
        return;
      }

      const isFromValet =
        this.$route.query.fromValet == 1 ||
        (this.$routerStack &&
          this.$routerStack.includes("/user/order/fast/qqgpay"));
      if (isFromValet) {
        this.$router.replace({
          path: "/user/order/fast/qqgpay",
          query: { orderSn },
        });
      } else {
        this.$router.replace({
          path: "/order/detail",
          query: { orderSn },
        });
      }
    },
  },
};
</script>

<style lang="less" scoped>
.pay_type {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 16px;
  .pay_type_left {
    display: flex;
    align-items: center;
  }
  .stripe_font {
    font-size: 14px;
    color: #333;
    vertical-align: middle;
  }
  .select_icon {
    display: block;
    width: 18px;
    height: 18px;
  }
  .stripe_icon {
    width: 31px;
    height: 24px;
    margin-right: 12px;
    object-fit: contain;
  }
}

/* Stripe 卡输入区容器 */
.stripe_card_wrap {
  padding: 0 16px 16px;
}

.stripe_loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  .stripe_spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 2px solid #e5e5e5;
    border-top-color: #bf9264;
    border-radius: 50%;
    animation: stripe-spin 0.6s linear infinite;
  }
  .stripe_loading_text {
    margin-left: 8px;
    font-size: 13px;
    color: #999;
  }
}

@keyframes stripe-spin {
  to {
    transform: rotate(360deg);
  }
}

.stripe_error_msg {
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  font-size: 12px;
  color: #ff4d4f;
  line-height: 18px;
}

/* 一站式 Payment Element 容器 */
.stripe_payment_element {
  margin-top: 4px;
  min-height: 150px;
}

/* 悬浮底栏定位容器 */
.bottom_btn_wrap {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  padding: 12px 16px;
  box-sizing: border-box;
  z-index: 100;
  /* 适配底部刘海安全区域 */
  padding-bottom: calc(12px + constant(safe-area-inset-bottom));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

/* 确认支付按钮 */
.save_btn {
  width: 100%;
  background: linear-gradient(90deg, #e1c49d 0%, #bf9264 100%);
  border-radius: 22px;
  padding: 13px 0;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(191, 146, 100, 0.15);
  line-height: 20px;
  &:active {
    opacity: 0.9;
  }
}

/* 按钮禁用态 */
.save_btn_disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
